import { computed, unref } from 'vue';
import useAcvStore from '@/composables/useAcvStore';

const COUNTRY_RESTRICTED_ISSUE_NAME = 'invalid_country';

const isCountryRestrictedIssue = (issue) => issue?.name?.toLowerCase()
  === COUNTRY_RESTRICTED_ISSUE_NAME;

export default function useAuctionAccess(_dealerIssues = []) {
  const dealerIssues = computed(() => unref(_dealerIssues) ?? []);
  const store = useAcvStore();

  const auctionAccessIssues = computed(() => store.state.auctionAccess.allIssues || []);
  const hasAuctionAccessIssues = computed(() => auctionAccessIssues.value.length > 0);
  const auctionAccessId = computed(() => store.state.auctionAccess.auctionAccessId);
  const hasBlockingIssues = computed(
    () => auctionAccessIssues.value.some((i) => i?.restrictions?.blockingIssue),
  );

  const aaPersonIssues = computed(() => store.getters.aaPersonIssues || []);

  const countryRestrictedIssue = computed(
    () => [...aaPersonIssues.value, ...dealerIssues.value].find(isCountryRestrictedIssue),
  );
  const isCountryRestricted = computed(() => !!countryRestrictedIssue.value);

  const nonCountryRestrictedPersonIssues = computed(
    () => aaPersonIssues.value.filter((issue) => !isCountryRestrictedIssue(issue)),
  );
  const nonCountryRestrictedDealerIssues = computed(
    () => dealerIssues.value.filter((issue) => !isCountryRestrictedIssue(issue)),
  );

  const hasBlockingPersonIssues = computed(
    () => nonCountryRestrictedPersonIssues.value.some((issue) => issue?.restrictions?.registration),
  );

  const hasBlockingDealershipIssues = computed(
    () => nonCountryRestrictedDealerIssues.value.some(
      (issue) => issue?.restrictions?.registration === true,
    ),
  );

  const hasRegistrationBlockingIssues = computed(
    () => hasBlockingDealershipIssues.value || hasBlockingPersonIssues.value,
  );

  const allIssues = computed(
    () => [...nonCountryRestrictedPersonIssues.value, ...nonCountryRestrictedDealerIssues.value],
  );

  const issueErrorText = computed(() => {
    if (!Array.isArray(allIssues.value) || !allIssues.value.length) {
      return '';
    }

    const [firstIssue] = allIssues.value;
    let sanitizedMessage = firstIssue.message?.slice(-1) === '.'
      ? firstIssue.message.slice(0, -1) : firstIssue.message;
    if (sanitizedMessage === undefined || sanitizedMessage === '') {
      sanitizedMessage = 'There is an issue with your AuctionACCESS Account';
    }
    if (allIssues.value.length === 1) {
      return `${sanitizedMessage}.`;
    }
    const numExtraIssues = allIssues.value.length - 1;
    return `${sanitizedMessage}, and ${numExtraIssues} other issue${numExtraIssues > 1 ? 's' : ''}.`;
  });

  const dealerEmail = computed(() => store.state.auctionAccess.dealerEmail);
  const maskedEmail = computed(() => store.state.auctionAccess.maskedEmail);
  const codeTtlSeconds = computed(() => store.state.auctionAccess.codeTtlSeconds);
  const last4PhotoId = computed(() => store.state.auctionAccess.last4PhotoId);
  const googleConversionInfo = computed(() => store.state.auctionAccess.googleConversionInfo ?? {});

  const aaFirstName = computed(() => store.state.auctionAccess.firstName);
  const aaLastName = computed(() => store.state.auctionAccess.lastName);

  const aaFullName = computed(
    () => [aaFirstName.value, aaLastName.value].filter(Boolean).join(' '),
  );

  const updateAAInfo = (info) => store.dispatch('updateAAInfo', info);

  return {
    aaFirstName,
    aaLastName,
    aaFullName,
    auctionAccessIssues,
    auctionAccessId,
    dealerEmail,
    maskedEmail,
    codeTtlSeconds,
    hasAuctionAccessIssues,
    hasBlockingIssues,
    aaPersonIssues,
    hasBlockingPersonIssues,
    hasBlockingDealershipIssues,
    hasRegistrationBlockingIssues,
    isCountryRestricted,
    allIssues,
    last4PhotoId,
    googleConversionInfo,
    issueErrorText,
    updateAAInfo,
  };
}
